import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that displays a product's Manufacturer Part Number (MPN).
 *
 * MPN is a custom variant metafield (mm-google-shopping.mpn), not one of
 * Shopify's built-in variant fields, so it isn't present on the `resource`
 * object the theme's productSelect event carries. Instead, this listens for
 * that event and reads the updated value from the freshly-fetched section
 * HTML (`detail.html`), which already contains the correct server-rendered
 * value for whichever variant was just selected — the same HTML other
 * listeners (price-per-item, quantity-rules, etc.) read from.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} mpnContainer - The container element for the MPN
 * @property {HTMLElement} mpn - The span element that displays the MPN text
 *
 * @extends {Component<Refs>}
 */
class ProductMpnComponent extends Component {
  requiredRefs = ['mpnContainer', 'mpn'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles the product select event and updates the MPN from the newly-fetched HTML.
   * @param {CustomEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, html, productId } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }
        if (productId && productId !== this.dataset.productId) {
          return;
        }
        if (!html) return;

        const newMpnEl = html.querySelector('[data-testid="product-mpn"]');
        const newMpnText = newMpnEl?.textContent?.trim() || '';

        if (newMpnText) {
          this.style.display = 'block';
          this.refs.mpn.textContent = newMpnText;
        } else {
          this.style.display = 'none';
          this.refs.mpn.textContent = '';
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-mpn] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-mpn-component')) {
  customElements.define('product-mpn-component', ProductMpnComponent);
}
